import styled from "styled-components"
import { Link } from "react-router-dom"
import SvgLogo from "../../../../app/assets/img/logo.svg"

// Footer Container
const FooterWrapper = styled.footer`
  background-color: ${({ theme }) => theme.backgroundSecondary};
  color: ${({ theme }) => theme.textPrimary};
  padding: 3rem 1.5rem 2rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem;
  text-align: center;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    text-align: left;
    padding: 4rem clamp(2rem, 5vw, 6rem) 2.5rem;
  }

  @media (min-width: 1100px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`

// Logo container
const Logo = styled(Link)`
  display: flex;
  align-items: center;
  font-size: 1.8rem;
  font-weight: bold;
  color: ${({ theme }) => theme.primary};
  text-decoration: none;

  svg {
    max-width: 250px;
    width: 100%;
    height: auto;
    max-height: 100px;
    margin: 0;
    transition: width 0.3s ease;
  }
`

// Individual Footer Section
const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;

  @media (min-width: 768px) {
    align-items: flex-start;
  }
`

// Section Title
const FooterTitle = styled.div`
  font-size: 1.15rem;
  font-weight: 600;
  color: ${({ theme }) => theme.primaryDark};
  margin: 0;
`

// Link Styles
const FooterLink = styled(Link)`
  color: ${({ theme }) => theme.textPrimary};
  font-size: 0.95rem;
  margin: 0.15rem 0;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: ${({ theme }) => theme.primaryLight};
  }
`

// Contact Information
const ContactInfo = styled.p`
  font-size: 0.95rem;
  margin: 0;

  a {
    color: ${({ theme }) => theme.primary};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`

const FooterButtonLink = styled(Link)`
  background: linear-gradient(135deg, ${({ theme }) => theme.primary} 0%, ${({ theme }) => theme.primaryDark} 100%);
  color: ${({ theme }) => theme.white};
  padding: 0.7rem 1.4rem;
  border-radius: 2px;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  justify-content: center;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(27, 117, 188, 0.25);
  }
`

const LocalitiesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 0.35rem;

  li {
    font-size: 0.95rem;
  }
`

const MapLink = styled.a`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.primary};
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`

const Footnote = styled.p`
  color: ${({ theme }) => theme.textPrimary};
  font-size: 0.85rem;
  font-weight: 500;
  text-align: center;
  margin: 0;
  background-color: ${({ theme }) => theme.backgroundSecondary};
  padding: 0.8rem 1rem;
  width: 100%;

  a {
    color: ${({ theme }) => theme.primaryDark};
    text-decoration: none;

    &:hover {
      color: ${({ theme }) => theme.primary};
    }
  }
`

const Footer = () => {
  const yearDate = new Date().getFullYear()

  return (
    <>
      <FooterWrapper data-nosnippet>
        <FooterSection>
          <Logo to="/" aria-label="TB Gestion Immobilière — accueil">
            <SvgLogo />
          </Logo>
          <FooterTitle>Gestion locale et proactive</FooterTitle>
          <p>
            Services de gestion locative pour les propriétaires de Portneuf et de la couronne ouest de Québec, avec un suivi clair et une présence
            locale.
          </p>
        </FooterSection>

        <FooterSection>
          <FooterTitle>Découvrir</FooterTitle>
          <FooterLink to="/services-gestion-immobiliere">Tous les services</FooterLink>
          <FooterLink to="/gestion-locative-portneuf">Gestion locative</FooterLink>
          <FooterLink to="/gestion-chalets-condos-portneuf">Chalets et condos</FooterLink>
          <FooterLink to="/guide-proprietaires-portneuf">Guides pour propriétaires</FooterLink>
          <FooterLink to="/a-propos">À propos</FooterLink>
          <FooterLink to="/se-connecter">Espace TB</FooterLink>
        </FooterSection>

        {/* Service Area Section */}
        <FooterSection>
          <FooterTitle>Territoire desservi</FooterTitle>
          <LocalitiesList>
            <li>Neuville et Pont-Rouge</li>
            <li>Donnacona et Cap-Santé</li>
            <li>Saint-Augustin-de-Desmaures</li>
            <li>Portneuf et la couronne ouest de Québec</li>
          </LocalitiesList>
          <MapLink
            href="https://www.google.com/maps/place/Neuville,+QC"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Voir Neuville sur Google Maps"
          >
            Voir sur Google Maps
          </MapLink>
        </FooterSection>

        {/* Contact Section */}
        <FooterSection>
          <FooterTitle>Contact</FooterTitle>
          <ContactInfo>
            Téléphone : <a href="tel:4186849004">(418) 684-9004</a>
          </ContactInfo>
          <ContactInfo>
            Courriel : <a href="mailto:info@tbgestionimmobiliere.com">info@tbgestionimmobiliere.com</a>
          </ContactInfo>
          <FooterButtonLink to="/nous-contacter">Nous contacter</FooterButtonLink>
          <FooterLink to="/faire-une-demande">Demande de consultation</FooterLink>
          <FooterLink to="/demande-de-location">Demande de location</FooterLink>
          <FooterLink to="/politique-de-confidentialite">Confidentialité</FooterLink>
          <FooterLink to="/conditions-utilisation">Conditions d’utilisation</FooterLink>
        </FooterSection>
      </FooterWrapper>
      <Footnote data-nosnippet>
        © {yearDate} TB Gestion Immobilière{" "}
        <a href="https://www.sor-seo.com" target="_blank" rel="noopener noreferrer" aria-label="Visiter le site de SOR SEO">
          | Design et développement par SOR SEO
        </a>
      </Footnote>
    </>
  )
}

export default Footer
